/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Login form presentational component — renders email/password inputs and submit button
 * @genesis_boundaries No state management, no business logic, no API calls
 * @genesis_ai_trail V9.0: Extracted from Login.tsx monolith during tactical DDD refactor.
 */

import { ArrowRight, Loader2, Lock, Mail } from 'lucide-react';
import React from 'react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';

import AuthFormInput from '@/pages/auth/components/AuthFormInput';
import SocialLoginButtons from '@/pages/auth/components/SocialLoginButtons';

interface LoginFormData {
    email: string;
    password: string;
}

interface LoginFormProps {
    register: UseFormRegister<LoginFormData>;
    errors: FieldErrors<LoginFormData>;
    isSubmitting: boolean;
    isInitializing: boolean;
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    onForgotPassword: () => void;
    onGoogleLogin: () => void;
    onGithubLogin: () => void;
}

export function LoginForm({
    register,
    errors,
    isSubmitting,
    isInitializing,
    onSubmit,
    onForgotPassword,
    onGoogleLogin,
    onGithubLogin,
}: LoginFormProps) {
    return (
        <>
            <form onSubmit={onSubmit} className="space-y-6">
                <AuthFormInput
                    type="email"
                    label="Email Address"
                    name="email"
                    register={register('email')}
                    placeholder="maria@family.com"
                    icon={Mail}
                    disabled={isInitializing || isSubmitting}
                    error={errors.email?.message}
                />

                <AuthFormInput
                    type="password"
                    label="Password"
                    name="password"
                    register={register('password')}
                    placeholder="••••••••"
                    icon={Lock}
                    disabled={isInitializing || isSubmitting}
                    minLength={6}
                    error={errors.password?.message}
                    showForgotPassword={true}
                    onForgotPassword={onForgotPassword}
                />

                <button
                    type="submit"
                    disabled={isInitializing || isSubmitting}
                    className="w-full h-14 bg-[var(--color-primary-base)] hover:bg-[var(--color-primary-hover)] disabled:bg-[var(--color-primary-base)]/50 text-[var(--color-content-inverse)] rounded-2xl text-lg font-semibold flex items-center justify-center gap-2 mt-4 transition-all active:scale-[0.98] shadow-google hover:shadow-google-lg"
                >
                    {isInitializing || isSubmitting ? (
                        <Loader2 className="animate-spin" size={24} />
                    ) : (
                        <>
                            <span>Sign In</span>
                            <ArrowRight size={20} />
                        </>
                    )}
                </button>
            </form>

            <SocialLoginButtons
                onGoogleLogin={onGoogleLogin}
                onGithubLogin={onGithubLogin}
                disabled={isInitializing || isSubmitting}
            />
        </>
    );
}
